import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BILLING_QUERY_KEY } from "./useBilling";
import apiClient from "../client";

export const MY_MEMBERSHIPS_QUERY_KEY = ["my-memberships"] as const;

/** A membership that gives the customer access now. The API leaves out ended ones. */
export interface CustomerMembershipDto {
  id: string;
  membershipTierId: string;
  tierName: string;
  platformName: string;
  /** #RRGGBB. */
  badgeColour: string;
  /** Null shows the first letter of the platform's name. */
  badgeLetters: string | null;
  /** Null while it runs on. Set when it is ending: staff ended it from a later day, or the customer cancelled it. */
  activeTo: string | null;
  /** Billed here, so the customer can cancel it themselves. */
  boughtHere: boolean;
  /** When the customer cancelled it, which they can take back until it ends. */
  cancelledAt: string | null;
  /** Staff cancelled it, so it's theirs to resume, not the customer's. */
  cancelledByStaff: boolean;
  tradingStrategyIds: string[];
}

interface ListMyMembershipsResponse {
  items: CustomerMembershipDto[];
}

export function useMyMemberships() {
  return useQuery<CustomerMembershipDto[]>({
    queryKey: MY_MEMBERSHIPS_QUERY_KEY,
    queryFn: async () => {
      const res = await apiClient.get<ListMyMembershipsResponse>(
        "/customers/me/memberships",
      );
      return res.data.items;
    },
  });
}

export interface CancelMembershipResponse {
  membershipExternalId: string;
  activeTo: string;
}

/** Ends one of our memberships the customer bought; access runs to the end of the paid period. */
export function useCancelMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tierId: string) => {
      const res = await apiClient.post<CancelMembershipResponse>(
        `/customers/me/memberships/${encodeURIComponent(tierId)}/cancel`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_MEMBERSHIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

export function useResumeMembership() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (tierId: string) => {
      const res = await apiClient.post(
        `/customers/me/memberships/${encodeURIComponent(tierId)}/resume`,
      );
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MY_MEMBERSHIPS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: BILLING_QUERY_KEY });
    },
  });
}

/** Ending, but the customer's own cancel, so they can take it back until it ends. */
export function canResumeMembership(
  membership: Pick<CustomerMembershipDto, "cancelledAt" | "cancelledByStaff" | "activeTo">,
): boolean {
  return (
    membership.cancelledAt !== null &&
    !membership.cancelledByStaff &&
    (membership.activeTo === null || new Date(membership.activeTo).getTime() > Date.now())
  );
}

/** The memberships whose tier includes the strategy. */
export function membershipsIncluding(
  strategyId: string,
  memberships: CustomerMembershipDto[],
): CustomerMembershipDto[] {
  return memberships.filter((membership) =>
    membership.tradingStrategyIds.includes(strategyId),
  );
}

export function isIncludedInMembership(
  strategyId: string,
  memberships: CustomerMembershipDto[],
): boolean {
  return membershipsIncluding(strategyId, memberships).length > 0;
}
